import { toast } from '@/components/ui/Toast/Toast';
import { GET_TIMESLOTS_PER_DAY_QUERY_KEY } from '@/features/scheduling/api/getTimeslotsPerDay';
import { useRescheduleRecurringAppointment } from '@/features/scheduling/api/rescheduleRecurringAppointment';
import { getMaxCalendarDate } from '@/features/scheduling/utils/getMaxCalendarDate';
import { getSelectedDeliveryCenterSchedulingDetails } from '@/features/scheduling/utils/getSelectedDeliveryCenterSchedulingDetails';
import { getStudentEnrollmentDetails } from '@/features/scheduling/utils/getStudentEnrollmentDetails';
import { getRecurrenceEndDateOptions, getRecurrenceEndDatePresetKeys } from '@/features/scheduling/utils/recurrenceEndDateOptions';
import { useTimeFor } from '@/hooks/useTimeFor';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import { useAuthStore } from '@/stores/authStore';
import type { RecurrencePattern } from '@/types/scheduling';
import { centerDayFromKey, toCenterDayKey, toUtcIsoString } from '@/utils/timezone';
import { zodResolver } from '@hookform/resolvers/zod';
import { useIsFetching } from '@tanstack/react-query';
import { endOfDay, format, isEqual, subHours } from 'date-fns';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useRescheduleFlow } from '../../context/useRescheduleFlow';

export const useDateAndTimeStep = () => {
  const { t: tUtils } = useTranslation('utils');
  const { t: tInterval } = useTranslation('scheduling', { keyPrefix: 'interval' });
  const { t: tScheduleStep } = useTranslation('scheduling', { keyPrefix: 'scheduleDialog.steps.dateAndTimeStep' });
  const { translateError } = useErrorTranslation();

  const { userData } = useAuthStore();
  const userDataStudents = userData?.students ?? [];

  const { timeZone: centerTimeZone, zoned, now } = useTimeFor('sessionTime');

  const { data, goToNextStep, updateData, closeRescheduleDialog, goToStep, setErrorType } = useRescheduleFlow();

  const formSchema = z
    .object({
      newDateAndTime: z.string(),
      applyToFollowingSessions: z.boolean(),
      recurrencePattern: z.string().optional(),
      endDate: z.string().optional(),
      customEndDate: z.string().optional(),
    })
    .refine(
      (data) => {
        return data.newDateAndTime && data.newDateAndTime.length > 0;
      },
      {
        message: '',
        path: ['newDateAndTime'],
      },
    )
    .refine(
      (formData) => {
        if (!data.appointment?.appointment_date || !formData.newDateAndTime) return true;
        return !isEqual(new Date(formData.newDateAndTime), new Date(data.appointment.appointment_date));
      },
      {
        message: tUtils('validation.sameAsOriginal'),
        path: ['newDateAndTime'],
      },
    )
    .refine(
      (data) => {
        if (data.applyToFollowingSessions) {
          return data.recurrencePattern && data.recurrencePattern.length > 0;
        }
        return true;
      },
      {
        message: tUtils('validation.required'),
        path: ['recurrencePattern'],
      },
    )
    .refine(
      (data) => {
        if (data.applyToFollowingSessions) {
          return data.endDate && data.endDate.length > 0;
        }
        return true;
      },
      {
        message: tUtils('validation.required'),
        path: ['endDate'],
      },
    )
    .refine(
      (data) => {
        if (data.applyToFollowingSessions && data.endDate === 'custom') {
          return data.customEndDate && data.customEndDate.length > 0;
        }
        return true;
      },
      {
        message: tUtils('validation.noDateSelected'),
        path: ['customEndDate'],
      },
    );

  const presetEndDateKeys = getRecurrenceEndDatePresetKeys(now(), centerTimeZone);
  const isPresetEndDate = (dayKey: string) => presetEndDateKeys.includes(dayKey);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newDateAndTime: (data.newDateAndTime || data.appointment?.appointment_date) ?? '',
      applyToFollowingSessions: data.applyToFollowingSessions,
      recurrencePattern: data.recurring.recurrencePattern ?? data.appointment?.recurring_schedule?.recurrence_pattern ?? '',
      endDate: (() => {
        const storedEndDate = data.recurring.endDate;
        if (!storedEndDate) {
          // For recurring sessions, default to 'custom' to show the calendar
          return data.appointment?.recurring_schedule ? 'custom' : '';
        }

        return isPresetEndDate(storedEndDate) ? storedEndDate : 'custom';
      })(),
      customEndDate: (() => {
        const storedEndDate = data.recurring.endDate;
        const recurrenceEndDate = data.appointment?.recurring_schedule?.effective_until;

        if (!storedEndDate) {
          return recurrenceEndDate ? toCenterDayKey(recurrenceEndDate, centerTimeZone) : '';
        }

        return isPresetEndDate(storedEndDate) ? '' : storedEndDate;
      })(),
    },
  });

  const applyToFollowingSessions = form.watch('applyToFollowingSessions');
  const selectedDateTime = form.watch('newDateAndTime');

  const showRecurringOptions = data.appointment?.recurring_schedule && applyToFollowingSessions;

  const selectedWeekday = (() => {
    if (!selectedDateTime) return '';
    try {
      return format(zoned(selectedDateTime), 'EEEE') + 's';
    } catch {
      return '';
    }
  })();

  const getRepeatOptions = (): { value: RecurrencePattern; label: string }[] => {
    if (!selectedWeekday) {
      return [
        { value: 'weekly', label: tInterval('weekly') },
        { value: 'biweekly', label: tInterval('biweekly') },
        { value: 'fourWeekly', label: tInterval('fourWeekly') },
      ];
    }

    return [
      { value: 'weekly', label: `${tInterval('weekly')} ${tInterval('on')} ${selectedWeekday}` },
      { value: 'biweekly', label: `${tInterval('biweekly')} ${tInterval('on')} ${selectedWeekday}` },
      { value: 'fourWeekly', label: `${tInterval('fourWeekly')} ${tInterval('on')} ${selectedWeekday}` },
    ];
  };
  const repeatOptions = getRepeatOptions();

  const appointmentStudentConfig = data.config.data?.students.find((studentConfig) => studentConfig.id === data.appointment?.student_id);
  const appointmentEnrollment =
    data.appointment && data.config.data?.students
      ? getStudentEnrollmentDetails(data.config.data.students, data.appointment.student_id, data.appointment.enrollment_id)
      : undefined;

  const appointmentStudent = userDataStudents.find((student) => student.id === data.appointment?.student_id);

  const centerDetails =
    appointmentStudentConfig && appointmentStudent?.id && data.appointment?.delivery_type
      ? getSelectedDeliveryCenterSchedulingDetails([appointmentStudentConfig], appointmentStudent.id, data.appointment.delivery_type)
      : undefined;

  const maxRecurringDate: Date | undefined =
    appointmentEnrollment?.enrollmentEndDate && centerDetails?.max_advance_booking_recurring
      ? getMaxCalendarDate(appointmentEnrollment?.enrollmentEndDate, centerDetails.max_advance_booking_recurring, centerTimeZone)
      : undefined;

  const endDateOptions = getRecurrenceEndDateOptions({
    today: now(),
    timeZone: centerTimeZone,
    firstSession: zoned(selectedDateTime),
    maxRecurringDate,
  }).map(({ value, labelKey }) => ({ value, label: tScheduleStep(labelKey) }));

  const selectedEndDate = form.watch('endDate');
  const isCustomEndDate = selectedEndDate === 'custom';

  const { mutate: rescheduleAppointment, isPending: isReschedulingLoading } = useRescheduleRecurringAppointment({
    options: {
      onSuccess: (data, payload) => {
        updateData('rescheduledAppointmentDetails', data);

        const isPlan = payload.isPlan ?? false;

        if (!isPlan && (!data?.booked_appointments || data?.booked_appointments?.length === 0)) {
          setErrorType('fullyBooked');
          goToStep('error');
          return;
        }

        goToNextStep();
      },
      onError: (error) => {
        if (error.errors?.some((e) => e.code === 'APPOINTMENT_RESCHEDULE_IS_LATE')) {
          goToStep('lateReschedule');
          return;
        }

        if (error.errors?.some((e) => e.code === 'TIMESLOT_NOT_AVAILABLE')) {
          setErrorType('fullyBooked');
          goToStep('error');
          return;
        }

        toast({
          variant: 'destructive',
          description: translateError(error),
        });
      },
    },
  });

  const isTimeslotsFetching = useIsFetching({
    queryKey: [GET_TIMESLOTS_PER_DAY_QUERY_KEY],
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    if (!values.newDateAndTime) {
      toast({
        variant: 'destructive',
        description: tUtils('validation.noDateSelected'),
      });
      return;
    }

    if (isTimeslotsFetching) {
      toast({
        variant: 'destructive',
        description: tUtils('validation.noTimeSelected'),
      });
      return;
    }

    if (!data.appointment) {
      throw new Error('Missing appointment data');
    }

    void captureAnalyticsEvent('rescheduling_date_selected', {
      appointment_id: data.appointment.appointment_id,
      original_start_time: data.appointment.appointment_date,
      new_start_time: values.newDateAndTime,
      apply_to_following_sessions: values.applyToFollowingSessions,
      recurrence_pattern: values.recurrencePattern,
      end_date: values.endDate === 'custom' ? values.customEndDate : values.endDate,
    });
    updateData('newDateAndTime', values.newDateAndTime);
    updateData('applyToFollowingSessions', values.applyToFollowingSessions);

    if (values.applyToFollowingSessions) {
      const endDateValue = values.endDate === 'custom' ? values.customEndDate : values.endDate;
      const endDateIso = endDateValue ? toUtcIsoString(endOfDay(centerDayFromKey(endDateValue, centerTimeZone))) : undefined;

      updateData('recurring', {
        recurrencePattern: values.recurrencePattern as RecurrencePattern | undefined,
        endDate: endDateValue,
      });

      if (!values.recurrencePattern || !values.endDate) {
        toast({
          variant: 'destructive',
          description: tUtils('validation.noRecurrencePatternSelected'),
        });
        return;
      }

      rescheduleAppointment({
        appointmentId: data.appointment?.appointment_id,
        studentId: data.appointment?.student_id,
        enrollmentId: data.appointment?.enrollment_id,
        targetDate: values.newDateAndTime,
        isPlan: true,
        recurringSettings: {
          recurrencePattern: values.recurrencePattern as RecurrencePattern,
          endDate: endDateIso!,
        },
      });
    } else {
      updateData('recurring', {});
      goToNextStep();
    }
  };

  const minCancellationWindow = centerDetails?.minimum_cancellation_window_hours ?? 0;

  const freeCancellationUntil = data.appointment?.appointment_date && subHours(new Date(data.appointment.appointment_date), minCancellationWindow);

  return {
    form,
    onSubmit,
    data,
    showRecurringOptions,
    appointmentEnrollment,
    repeatOptions,
    endDateOptions,
    isCustomEndDate,
    closeRescheduleDialog,
    freeCancellationUntil,
    appointmentStudent,
    centerDetails,
    maxRecurringDate,
    isReschedulingLoading,
  } as const;
};
